import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import moment from 'moment'

import DatePicker from '../../common/form/DatePicker'
import CheckBox from '../../common/form/CheckBox'
import FiltersPopover from '../../common/FiltersPopover/FiltersPopover'
import CollapsibleFilterSection from '../../common/FiltersPopover/CollapsibleFilterSection'
import ProgramsEventsModal from './ProgramsEventsModal'

import { GENDER_OPTIONS } from '../../../misc/Constants'
import { useTranslated } from '../../../i18n'
import { translateOptions } from '../../../utility/i18nConverters'
import { getCommunityCategories } from '../../../actions/programCategoryActions'
import { countFilterChanges } from '../hooks/useStudentsFilters'

import classes from './StudentsFiltersPopover.module.scss'

const ENROLLMENT_STATUSES = ['active', 'future', 'waitlist', 'inactive']
const LEVELS = ['1', '2', '3', '4', '5', '6', '6+']

const AGE_OPTIONS = Array.from({ length: 118 }, (_, i) => i + 3)

// eslint-disable-next-line max-lines-per-function
const StudentsFiltersPopover = ({
  isOpen,
  toggle,
  currentFilters,
  initialFilters,
  onApply,
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const categories = useSelector(state => state.dashboard.communityCategories)
  const genderOptions = useTranslated(GENDER_OPTIONS, translateOptions)

  const [staged, setStaged] = useState(currentFilters)
  const [isProgramsModalOpen, setIsProgramsModalOpen] = useState(false)
  const [selectedPrograms, setSelectedPrograms] = useState([])
  const [selectedEvents, setSelectedEvents] = useState([])

  useEffect(() => {
    if (isOpen) {
      dispatch(getCommunityCategories())
    }
  }, [isOpen, dispatch])

  const toggleArrayValue = (key, value) => {
    setStaged(prev => {
      const arr = prev[key]
      const next = arr.includes(value) ? arr.filter(v => v !== value) : [...arr, value]
      return { ...prev, [key]: next }
    })
  }

  const handleClearAll = () => {
    setStaged(initialFilters)
    setSelectedPrograms([])
    setSelectedEvents([])
    onApply(initialFilters)
    toggle()
  }

  const pendingFilters = {
    ...staged,
    courseIds: selectedPrograms.map(p => p.id),
    eventIds: selectedEvents.map(e => e.id),
  }

  const pendingCount = countFilterChanges(currentFilters, pendingFilters)

  const handleApply = () => {
    onApply(pendingFilters)
    toggle()
  }

  const handleProgramsEventsConfirm = (programs, events) => {
    setSelectedPrograms(programs)
    setSelectedEvents(events)
  }

  const handleAgeChange = (key, value) => {
    setStaged(prev => ({ ...prev, [key]: value ? Number(value) : null }))
  }

  const isAllLevels = staged.levels.length === 0

  const handleAllLevels = () => {
    setStaged(prev => ({ ...prev, levels: [] }))
  }

  const programsEventsLabel = () => {
    const parts = [
      { count: selectedPrograms.length, key: 'members.filtersPopover.programCount' },
      { count: selectedEvents.length, key: 'members.filtersPopover.eventCount' },
    ].filter(p => p.count > 0).map(p => t(p.key, { count: p.count }))
    return parts.length > 0 ? t('members.filtersPopover.itemsSelected', { items: parts.join(', ') }) : null
  }

  return (
    <>
      <FiltersPopover
        isOpen={isOpen}
        toggle={toggle}
        target='filtersButton'
        pendingCount={pendingCount}
        onApply={handleApply}
        onClearAll={handleClearAll}
      >
        <CollapsibleFilterSection title={t('members.columns.enrollmentStatus')}>
          <div className={classes.checkboxGroup}>
            {ENROLLMENT_STATUSES.map(status => (
              <CheckBox
                key={status}
                id={`filter-enrollment-${status}`}
                isSelected={staged.enrollmentStatuses.includes(status)}
                onClick={() => toggleArrayValue('enrollmentStatuses', status)}
                label={t(`members.enrollmentStatuses.${status}`)}
                size='sm'
              />
            ))}
          </div>
        </CollapsibleFilterSection>

        <CollapsibleFilterSection title={t('common.field.gender')}>
          <div className={classes.checkboxGroup}>
            {genderOptions.map(gender => (
              <CheckBox
                key={gender.value}
                id={`filter-gender-${gender.value}`}
                isSelected={staged.genders.includes(gender.value)}
                onClick={() => toggleArrayValue('genders', gender.value)}
                label={gender.label}
                size='sm'
              />
            ))}
          </div>
        </CollapsibleFilterSection>

        <CollapsibleFilterSection title={t('members.filtersPopover.categories')}>
          <div className={classes.checkboxGroup}>
            {categories?.map(category => (
              <CheckBox
                key={category.id}
                id={`filter-category-${category.id}`}
                isSelected={staged.categoryIds.includes(category.id)}
                onClick={() => toggleArrayValue('categoryIds', category.id)}
                label={category.name}
                size='sm'
              />
            ))}
          </div>
        </CollapsibleFilterSection>

        <CollapsibleFilterSection title={t('members.filtersPopover.programsEvents')}>
          <button
            className={classes.programsEventsButton}
            onClick={() => setIsProgramsModalOpen(true)}
          >
            {programsEventsLabel()
              ? <span className={classes.programsEventsSelected}>{programsEventsLabel()}</span>
              : t('members.filtersPopover.chooseFromLibrary')
            }
          </button>
        </CollapsibleFilterSection>

        <CollapsibleFilterSection title={t('members.filtersPopover.dateJoined')}>
          <div className={classes.dateRow}>
            <div className={classes.dateField}>
              <span className={classes.fieldLabel}>{t('common.field.startDate')}</span>
              <DatePicker
                selected={staged.dateJoinedFrom ? moment(staged.dateJoinedFrom).toDate() : null}
                onChange={(date) => setStaged(prev => ({ ...prev, dateJoinedFrom: date ? moment(date).format('YYYY-MM-DD') : null }))}
                dateFormat='MM/dd/yyyy'
                placeholderText={t('members.filtersPopover.selectDate')}
                isClearable
              />
            </div>
            <div className={classes.dateField}>
              <span className={classes.fieldLabel}>{t('common.field.endDate')}</span>
              <DatePicker
                selected={staged.dateJoinedTo ? moment(staged.dateJoinedTo).toDate() : null}
                onChange={(date) => setStaged(prev => ({ ...prev, dateJoinedTo: date ? moment(date).format('YYYY-MM-DD') : null }))}
                dateFormat='MM/dd/yyyy'
                placeholderText={t('members.filtersPopover.selectDate')}
                isClearable
              />
            </div>
          </div>
        </CollapsibleFilterSection>

        <CollapsibleFilterSection title={t('members.filtersPopover.ageRange')}>
          <div className={classes.dateRow}>
            <div className={classes.dateField}>
              <label htmlFor='filter-age-from' className={classes.fieldLabel}>{t('members.filtersPopover.lowest')}</label>
              <select
                id='filter-age-from'
                className={classes.selectInput}
                value={staged.ageFrom ?? ''}
                onChange={(e) => handleAgeChange('ageFrom', e.target.value)}
              >
                <option value=''>{t('members.filtersPopover.any')}</option>
                {AGE_OPTIONS.map(age => (
                  <option key={age} value={age}>{age}</option>
                ))}
              </select>
            </div>
            <div className={classes.dateField}>
              <label htmlFor='filter-age-to' className={classes.fieldLabel}>{t('members.filtersPopover.highest')}</label>
              <select
                id='filter-age-to'
                className={classes.selectInput}
                value={staged.ageTo ?? ''}
                onChange={(e) => handleAgeChange('ageTo', e.target.value)}
              >
                <option value=''>{t('members.filtersPopover.any')}</option>
                {AGE_OPTIONS.map(age => (
                  <option key={age} value={age}>{age}</option>
                ))}
              </select>
            </div>
          </div>
        </CollapsibleFilterSection>

        <CollapsibleFilterSection title={t('members.filtersPopover.levels')}>
          <div className={classes.levelsGrid}>
            {LEVELS.map(level => (
              <CheckBox
                key={level}
                id={`filter-level-${level}`}
                isSelected={staged.levels.includes(level)}
                onClick={() => toggleArrayValue('levels', level)}
                label={t('members.filtersPopover.levelBadge', { level })}
                size='sm'
              />
            ))}
            <CheckBox
              id='filter-level-all'
              isSelected={isAllLevels}
              onClick={handleAllLevels}
              label={t('members.filtersPopover.allLevels')}
              size='sm'
            />
          </div>
        </CollapsibleFilterSection>
      </FiltersPopover>

      {isProgramsModalOpen && (
        <ProgramsEventsModal
          isOpen={isProgramsModalOpen}
          onClose={() => { setIsProgramsModalOpen(false); toggle() }}
          initialPrograms={selectedPrograms}
          initialEvents={selectedEvents}
          onConfirm={handleProgramsEventsConfirm}
        />
      )}
    </>
  )
}

StudentsFiltersPopover.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  currentFilters: PropTypes.object.isRequired,
  initialFilters: PropTypes.object.isRequired,
  onApply: PropTypes.func.isRequired,
}

export default StudentsFiltersPopover
